/**
 * Qué ficheros de certificación MITECO está viendo la consola, y cuándo entró el último desde Drive.
 *
 * Va debajo del filtro "Periodo | Fichero" y SOLO se mira: Ops lo abre para comprobar que los
 * ficheros que subió ayer ya están, y nada de aquí acota el preview (ver `../ficheros.ts` para el
 * porqué de cada decisión). Plegado por defecto: la línea de cabecera ya responde la pregunta
 * habitual ("¿cuándo fue la última carga y cuántos entraron?"); la lista es para cuando hace falta
 * buscar uno concreto.
 *
 * Los ficheros de la última carga llevan el tag ÚLTIMO y van arriba; el resto, por fecha de carga.
 */
import { Badge, Button, Collapse, Group, Loader, Table, Text } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconRefresh } from '@tabler/icons-react';
import { useState } from 'react';

import {
  describirLectura,
  formatearInstante,
  ordenarPorCarga,
  type FicherosLeidos as Datos,
} from '../ficheros';
import { formatearNumero } from '../formato';

export type EstadoFicheros =
  | { estado: 'cargando' }
  | { estado: 'error'; mensaje: string }
  | { estado: 'ok'; datos: Datos };

export function FicherosLeidos({
  estado,
  onRecargar,
}: {
  estado: EstadoFicheros;
  onRecargar?: () => void;
}) {
  const [abierto, setAbierto] = useState(false);

  if (estado.estado === 'cargando') {
    return (
      <Group gap="xs" className="ficheros-leidos">
        <Loader size="xs" />
        <Text size="xs" c="dimmed">
          Consultando los ficheros leídos de Drive…
        </Text>
      </Group>
    );
  }

  if (estado.estado === 'error') {
    // Discreto, no una alerta: esto es información de apoyo y no decide ningún pago. Pero se dice,
    // porque un bloque que desaparece en silencio es indistinguible de "no hay ficheros".
    return (
      <Group gap="xs" className="ficheros-leidos">
        <Text size="xs" c="orange">
          No se ha podido consultar la lista de ficheros leídos de Drive ({estado.mensaje}).
        </Text>
        {onRecargar && (
          <Button
            size="compact-xs"
            variant="subtle"
            leftSection={<IconRefresh size={12} />}
            onClick={onRecargar}
          >
            Reintentar
          </Button>
        )}
      </Group>
    );
  }

  const { datos } = estado;
  const ordenados = ordenarPorCarga(datos.ficheros);
  const hayLista = ordenados.length > 0;

  return (
    <div className="ficheros-leidos">
      <Group gap="xs" wrap="nowrap">
        <Button
          size="compact-xs"
          variant="subtle"
          color="gray"
          disabled={!hayLista}
          aria-expanded={abierto}
          aria-controls="ficheros-leidos-lista"
          leftSection={abierto ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          onClick={() => setAbierto((v) => !v)}
        >
          {describirLectura(datos)}
        </Button>
        {onRecargar && (
          <Button
            size="compact-xs"
            variant="subtle"
            color="gray"
            aria-label="Recargar la lista de ficheros"
            onClick={onRecargar}
          >
            <IconRefresh size={14} />
          </Button>
        )}
      </Group>

      <Collapse in={abierto && hayLista}>
        <Table
          id="ficheros-leidos-lista"
          fz="xs"
          verticalSpacing={4}
          mt="xs"
          withRowBorders={false}
          striped
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Fichero</Table.Th>
              <Table.Th>Periodo</Table.Th>
              <Table.Th>Leído de Drive</Table.Th>
              <Table.Th ta="right">Viajes pendientes</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ordenados.map((f) => (
              <Table.Tr key={f.driveFileName} data-ultimo={f.ultimo || undefined}>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <span>{f.driveFileName}</span>
                    {f.ultimo && (
                      <Badge size="xs" variant="filled" color="teal">
                        ÚLTIMO
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td c="dimmed">{f.certificationPeriod}</Table.Td>
                <Table.Td c="dimmed">{formatearInstante(f.cargadoEn) ?? '—'}</Table.Td>
                <Table.Td ta="right">{formatearNumero(f.nViajes)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Collapse>
    </div>
  );
}
